import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Plane, X, Search } from 'lucide-react';
import { Input } from './ui/input';

/**
 * HybridLocationInput - Smart location input that supports:
 * 1. CMS-managed airport selection (prioritized)
 * 2. Google Places Autocomplete for custom locations
 * 3. Manual text entry as fallback
 */
const HybridLocationInput = ({
  airports = [],
  value = '',
  onChange,
  placeholder = 'Search airport or enter address...',
  label = 'Location',
  required = false,
  testId = 'hybrid-location-input',
  disabled = false,
  prioritizeAirports = true,
  iconType = 'default',
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [justSelected, setJustSelected] = useState(false);
  const [showAirports, setShowAirports] = useState(true);
  
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const isGoogleSelectingRef = useRef(false);

  // Initialize Google Places Autocomplete on the VISIBLE input
  useEffect(() => {
    const initGoogleAutocomplete = () => {
      if (!window.google?.maps?.places || !inputRef.current || autocompleteRef.current) return;
      
      try {
        const autocomplete = new window.google.maps.places.Autocomplete(
          inputRef.current,
          {
            componentRestrictions: { country: ['tr', 'cy'] },
            fields: ['formatted_address', 'geometry', 'name', 'place_id'],
            types: ['establishment', 'geocode'],
          }
        );

        // Set bounds for Turkey
        const turkeyBounds = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(34.5, 25.5),
          new window.google.maps.LatLng(42.5, 45.0)
        );
        autocomplete.setBounds(turkeyBounds);

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          
          if (!place.geometry || !place.geometry.location) {
            console.warn('No geometry for place - user pressed Enter without selecting');
            return;
          }

          const displayValue = place.formatted_address || place.name;
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          console.log('Google Place selected:', { displayValue, lat, lng });

          isGoogleSelectingRef.current = true;
          
          // IMPORTANT: Force update both the DOM input and React state
          // Google Autocomplete updates the input DOM directly, but we need React state too
          if (inputRef.current) {
            inputRef.current.value = displayValue;
          }
          setInputValue(displayValue);
          setIsOpen(false);
          setShowAirports(false);
          setJustSelected(true);

          onChange({
            value: displayValue,
            lat: lat,
            lng: lng,
            type: 'custom',
            airportCode: null,
            placeId: place.place_id,
          });

          setTimeout(() => {
            setJustSelected(false);
            isGoogleSelectingRef.current = false;
          }, 500);
        });

        autocompleteRef.current = autocomplete;
        console.log('Google Places Autocomplete initialized on visible input');
      } catch (error) {
        console.error('Failed to initialize Google Places:', error);
      }
    };

    if (window.google?.maps?.places) {
      initGoogleAutocomplete();
    } else {
      const checkInterval = setInterval(() => {
        if (window.google?.maps?.places) {
          initGoogleAutocomplete();
          clearInterval(checkInterval);
        }
      }, 500);
      setTimeout(() => clearInterval(checkInterval), 10000);
      return () => clearInterval(checkInterval);
    }

    return () => {
      if (autocompleteRef.current && window.google) {
        try {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        } catch (e) {}
      }
    };
  }, [onChange]);

  // Sync external value changes
  useEffect(() => {
    if (value !== inputValue && !justSelected) {
      setInputValue(value);
    }
  }, [value, justSelected]);

  // Close OUR dropdown when clicking outside (Google's dropdown handles itself)
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Don't close if clicking on Google's pac-container
      if (e.target.closest('.pac-container')) return;
      
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter airports based on input
  const getFilteredAirports = useCallback((query) => {
    if (!query || query.length < 1) {
      // Show all airports when no query (up to 30)
      return airports.slice(0, 30);
    }

    const lowerQuery = query.toLowerCase();
    return airports.filter(airport => {
      const name = (airport.name || '').toLowerCase();
      const code = (airport.code || '').toLowerCase();
      const fullName = (airport.fullName || '').toLowerCase();
      const city = (airport.city || '').toLowerCase();

      return (
        name.includes(lowerQuery) ||
        code.includes(lowerQuery) ||
        fullName.includes(lowerQuery) ||
        city.includes(lowerQuery)
      );
    }).slice(0, 30);
  }, [airports]);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setHighlightedIndex(-1);

    // Update airport suggestions
    const filteredAirports = getFilteredAirports(newValue);
    setSuggestions(filteredAirports);
    
    // Show our airport dropdown if there are matching airports
    if (filteredAirports.length > 0) {
      setShowAirports(true);
      setIsOpen(true);
    } else {
      setShowAirports(false);
      setIsOpen(false); // Let Google's dropdown take over
    }
  };

  // Handle airport selection
  const handleAirportSelect = (airport) => {
    const displayValue = airport.fullName || `${airport.name} (${airport.code})`;
    
    setJustSelected(true);
    setInputValue(displayValue);
    
    // IMPORTANT: Update the DOM input directly
    if (inputRef.current) {
      inputRef.current.value = displayValue;
    }
    
    setIsOpen(false);
    setShowAirports(false);

    console.log('Airport selected:', { displayValue, lat: airport.lat, lng: airport.lng, code: airport.code });

    onChange({
      value: displayValue,
      lat: airport.lat,
      lng: airport.lng,
      type: 'airport',
      airportCode: airport.code,
      placeId: null,
    });

    setTimeout(() => {
      setJustSelected(false);
    }, 100);
  };

  // Handle input focus
  const handleFocus = () => {
    const filteredAirports = getFilteredAirports(inputValue);
    setSuggestions(filteredAirports);
    if (filteredAirports.length > 0) {
      setShowAirports(true);
      setIsOpen(true);
    }
  };

  // Handle input blur - if user typed without selecting, treat as manual entry
  const handleBlur = () => {
    // Delay to allow click events on dropdown items
    setTimeout(() => {
      if (justSelected || isGoogleSelectingRef.current) return;
      
      // If user has typed something but didn't select from dropdown
      if (inputValue && inputValue.trim().length >= 3) {
        console.log('Manual entry on blur:', inputValue);
        onChange({
          value: inputValue.trim(),
          lat: null,
          lng: null,
          type: 'manual',
          airportCode: null,
          placeId: null,
        });
      }
      setIsOpen(false);
      setShowAirports(false);
    }, 300);
  };

  // Handle keyboard navigation for airports
  const handleKeyDown = (e) => {
    if (!isOpen || !showAirports) return;
    
    const totalItems = suggestions.length;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev < totalItems - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : totalItems - 1));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      if (highlightedIndex < suggestions.length) {
        handleAirportSelect(suggestions[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Clear input
  const handleClear = (e) => {
    e.stopPropagation();
    setInputValue('');
    
    // IMPORTANT: Clear the DOM input too
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    
    setSuggestions(airports.slice(0, 5));
    setShowAirports(true);
    setIsOpen(true);
    onChange({
      value: '',
      lat: null,
      lng: null,
      type: null,
      airportCode: null,
      placeId: null,
    });
    inputRef.current?.focus();
  };

  // Get icon
  const getIcon = () => {
    if (iconType === 'pickup' || iconType === 'dropoff') {
      return <MapPin className="h-4 w-4 text-orange-500" />;
    }
    return <Search className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <div ref={containerRef} className="relative w-full" data-testid={testId}>
      {/* Input */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
          {getIcon()}
        </div>
        
        <Input
          ref={inputRef}
          type="text"
          defaultValue={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-10 pr-10 bg-card border-border text-card-foreground placeholder:text-muted-foreground"
          autoComplete="off"
          data-testid={`${testId}-input`}
        />

        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground z-10"
            data-testid={`${testId}-clear`}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Airport Dropdown (above Google's dropdown) */}
      {isOpen && showAirports && suggestions.length > 0 && (
        <div 
          className="absolute z-[9999] w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-96 overflow-y-auto"
          data-testid={`${testId}-dropdown`}
          style={{ zIndex: 9999 }}
        >
          <div className="p-1">
            <div className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
              Airports
            </div>
            {suggestions.map((airport, index) => (
              <button
                key={airport.code || index}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent blur
                  handleAirportSelect(airport);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded transition-colors ${
                  highlightedIndex === index
                    ? 'bg-orange-100 text-orange-600'
                    : 'hover:bg-gray-100 text-gray-900'
                }`}
                data-testid={`${testId}-airport-${airport.code}`}
              >
                <Plane className="h-4 w-4 text-orange-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate text-gray-900">
                    {airport.name} ({airport.code})
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {airport.city}, {airport.country}
                  </div>
                </div>
              </button>
            ))}
          </div>
          
          {/* Hint for custom locations */}
          <div className="border-t border-gray-200 p-2">
            <div className="text-xs text-gray-500 text-center">
              Type more for hotels, addresses (Google Places)
            </div>
          </div>
        </div>
      )}

      {/* Google Autocomplete styling - FIXED for light background */}
      <style>{`
        .pac-container {
          z-index: 10000 !important;
          background-color: #ffffff !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 0.375rem !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1) !important;
          font-family: inherit !important;
          margin-top: 4px !important;
        }
        .pac-item {
          padding: 10px 12px !important;
          color: #1f2937 !important;
          background-color: #ffffff !important;
          border-top: 1px solid #e5e7eb !important;
          cursor: pointer !important;
          line-height: 1.4 !important;
        }
        .pac-item:first-child {
          border-top: none !important;
        }
        .pac-item:hover,
        .pac-item-selected,
        .pac-item.pac-item-selected {
          background-color: #fff7ed !important;
          color: #1f2937 !important;
        }
        .pac-icon {
          display: none !important;
        }
        .pac-item-query {
          color: #1f2937 !important;
          font-weight: 500 !important;
          font-size: 14px !important;
        }
        .pac-item-query .pac-matched {
          color: #ea580c !important;
          font-weight: 600 !important;
        }
        .pac-matched {
          color: #ea580c !important;
          font-weight: 600 !important;
        }
        .pac-logo::after {
          margin-top: 8px !important;
          background-color: #ffffff !important;
        }
        /* Secondary text (address details) */
        .pac-item span:not(.pac-item-query):not(.pac-matched) {
          color: #6b7280 !important;
        }
      `}</style>
    </div>
  );
};

export default HybridLocationInput;
